import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sea-snag/ui';
import { HTTPError } from 'ky';
import { Send, SkipForward } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { invitationsApi } from '../../lib/api-client';
import { EmailTagInput } from './email-tag-input';

interface StepInviteMembersProps {
  orgPid: string;
  onComplete: () => void;
}

export function StepInviteMembers({ orgPid, onComplete }: StepInviteMembersProps) {
  const [emails, setEmails] = useState<string[]>([]);
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (emails.length === 0) return;

    setSending(true);

    const results = await Promise.allSettled(
      emails.map((email) => invitationsApi.create(orgPid, { email, role }))
    );

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < results.length; i++) {
      const result = results[i]!;
      if (result.status === 'fulfilled') {
        successCount++;
      } else {
        failCount++;
        const err = result.reason as unknown;
        if (err instanceof HTTPError) {
          const body = (await err.response.json().catch(() => ({}))) as { message?: string };
          toast.error(`Failed to invite ${emails[i]}: ${body.message || 'Unknown error'}`);
        }
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} invitation${successCount > 1 ? 's' : ''} sent successfully`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} invitation${failCount > 1 ? 's' : ''} failed`);
    }

    setSending(false);
    onComplete();
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold">Invite your team</h3>
        <p className="text-sm text-muted-foreground">
          Add team members to collaborate on fleet management. You can always invite more later.
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Email addresses</Label>
          <EmailTagInput value={emails} onChange={setEmails} placeholder="colleague@example.com" />
        </div>

        <div className="space-y-2">
          <Label>Role for all invitees</Label>
          <Select value={role} onValueChange={(v) => setRole(v as 'admin' | 'member')}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Button
          size="lg"
          className="w-full"
          onClick={handleSend}
          disabled={emails.length === 0 || sending}
        >
          {sending ? (
            'Sending invitations...'
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Send {emails.length > 0 ? `${emails.length} ` : ''}Invitation
              {emails.length !== 1 ? 's' : ''} & Continue
            </>
          )}
        </Button>
        <Button
          variant="ghost"
          size="lg"
          className="w-full"
          onClick={onComplete}
          disabled={sending}
        >
          <SkipForward className="mr-2 h-4 w-4" />
          Skip for now
        </Button>
      </div>
    </div>
  );
}
